// src/features/feature1/feature1Slice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchBrands,
  fetchCategories,
  fetchProducts,
  fetchTopSellingProducts,
  fetchHealthCategories,
  fetchOffers,
  fetchofferProducts,
} from "./feature1API";
import { fetchProfileData } from "../../API/axiosClient";
import axios from "axios";

// Async thunks for fetching data
export const getBrands = createAsyncThunk("feature1/getBrands", async () => {
  const response = await fetchBrands();
  return response.data.data;
});

export const getCategories = createAsyncThunk(
  "feature1/getCategories",
  async () => {
    const response = await fetchCategories();
    return response.data.data;
  }
);

export const getProducts = createAsyncThunk(
  "feature1/getProducts",
  async () => {
    const response = await fetchProducts();
    return response.data.data;
  }
);

export const getHealthCategories = createAsyncThunk(
  "feature1/getHealthCategories",
  async () => {
    const response = await fetchHealthCategories();
    return response.data.data; // Ensure this matches the structure of the API response
  }
);

export const getTopSellingProducts = createAsyncThunk(
  "feature1/getTopSellingProducts",
  async () => {
    // Check if data exists in localStorage
    const storedData = localStorage.getItem("topSellingProducts");

    if (storedData) {
      // If data is available in localStorage, return it
      return JSON.parse(storedData);
    }

    // Otherwise, fetch from API
    const response = await fetchTopSellingProducts();
    const data = response.data.result;

    // Save the fetched data to localStorage
    // localStorage.setItem("topSellingProducts", JSON.stringify(data));

    return data;
  }
);

export const getUserId = createAsyncThunk("feature1/getUserId", async () => {
  const userData = await fetchProfileData();
  return userData._id;
});

export const getOffers = createAsyncThunk("feature1/getOffers", async () => {
  const response = await fetchOffers();
  return response.data.data; // Return only the data property
});

export const getcouponOffers = createAsyncThunk(
  "feature1/getcouponOffers",
  async ({ coupon_code }) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/coupon/offers`,
      { coupon_code }
    );
    return response.data; // Ensure this returns the array of coupon offers
  }
);

// src/features/feature1/feature1Slice.js
export const getOfferProducts = createAsyncThunk(
  "feature1/getOfferProducts",
  async (_, { getState }) => {
    const { page, itemsPerPage } = getState().feature1; // Assuming pagination is managed in the state
    const response = await fetchofferProducts(50, page, itemsPerPage);
    return response.data.data;
  }
);

// Initial state
const initialState = {
  brands: [],
  categories: [],
  products: [],
  getofferProducts: [],
  healthcategories: [],
  topSellingProducts: [],
  offers: [],
  couponOffers: [],
  userId: null,
  status: "idle",
  error: null,
};

// Slice
const feature1Slice = createSlice({
  name: "feature1",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBrands.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getBrands.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.brands = action.payload;
      })
      .addCase(getBrands.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getCategories.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.categories = action.payload;
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload;
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Handle top-selling products
      .addCase(getTopSellingProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getTopSellingProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.topSellingProducts = action.payload;
      })
      .addCase(getTopSellingProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getUserId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userId = action.payload;
      })
      .addCase(getUserId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getHealthCategories.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getHealthCategories.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.healthcategories = action.payload;
      })
      .addCase(getHealthCategories.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getOffers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.offers = action.payload;
      })
      .addCase(getOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getOfferProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getOfferProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.offers = action.payload; // Store fetched offer products
      })
      .addCase(getOfferProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getcouponOffers.fulfilled, (state, action) => {
        state.couponOffers = action.payload; // Make sure this is correctly set to an array of coupon offers
      })
      .addCase(getcouponOffers.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export default feature1Slice.reducer;
